import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalHeader,
  Tile,
} from "@carbon/react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import CancelOrderDialog from "./CancelOrderDialog";
import { countOfferingSuppliers, useJointBidOffers } from "./useJointBidOffers";
import {
  AwardJoint,
  GoBackArrow,
  Loader,
  Offer,
  Chat,
  AlertBanner,
  Pill,
  SecondRoundModal,
} from "../../components";
import SecondRoundTag from "../../components/UI/SecondRoundTag/SecondRoundTag";
import InviteSupplier from "../../components/Material/InviteSupplier/InviteSupplier";
import CreateOcModal from "../PurchaseOrders/CreateOc/CreateOcModal";
import { useAwardableNegotiations } from "../../hooks/useAwardableNegotiations";
import { useID } from "../../hooks";
import OffersOnMultipleOrders from "./OffersOnMultipleOrders";
import SuppliersSelected from "./SuppliersSelected";
import BidHeader from "./BidHeader";
import BidResultTable from "./BidResultTable";
import BidToolbar from "./BidToolbar";
import BidAwardedTabs from "./BidAwardedTabs";
import { OrderConditionsTile, OfferSummaryTile } from "./BidSummaryTiles";
import { JointMaterialsOverviewTile } from "./MaterialsOverview";
import { OfferResponse, OrderState } from "../../types";
import { useInvitedOrderSuppliers } from "../../hooks/useSuppliers";
import ActivityTimeline from "../../components/ActivityTimeline/ActivityTimeline";
import {
  useDownloadJointBidReport,
  useDownloadOrderComparison,
  useDeleteMultipleOrders,
  useJointOrder,
  useOrderActivityLog,
  useOrderSupplierActivity,
} from "../../hooks/useOrders";
import {
  useCreateChat,
  useCreateMessage,
  useMultipleOrderChat,
} from "../../hooks/useChat";
import {
  useAcceptOffer,
  useMultipleOffersOnOrders,
} from "../../hooks/useOffers";
import { queryKeys } from "../../hooks/queryKeys";
import { useUser } from "../../context";
import { useNotification } from "../../context";
import { ABILITIES, formatBiddingDateTime } from "../../utils";
import "./bid.scss";
import ServicesToQuoteCard from "../../components/ServicesToQuoteCard/ServicesToQuoteCard";
import { servicesToQuoteOf } from "../Order/serviceDetail";

const JointBuyerBid = () => {
  const { isCompliant, can } = useUser();
  const canManageOrder = can(ABILITIES.CREATE_ORDERS);
  const { openNotification } = useNotification();

  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const id = useID();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [openSlide, setOpenSlide] = useState(false);
  const [openInviteModal, setOpenInviteModal] = useState(false);
  const [cancelMessage, setCancelMessage] = useState("");
  const [itemToSlide, setItemToSlide] = useState<OfferResponse[] | null>(null);
  const [isAward, setIsAward] = useState(false);
  const [isViewingAward, setIsViewingAward] = useState(false);
  const [secondRoundOpen, setSecondRoundOpen] = useState(false);
  const [createOcOpen, setCreateOcOpen] = useState(false);

  const queryClient = useQueryClient();

  const multipleOrderId = searchParams.get("multipleOrderId");

  const { data: multipleOrderChat, isLoading: isLoadingChat } =
    useMultipleOrderChat(multipleOrderId || "");

  const { data: jointOrder, isLoading: isLoadingOrders } = useJointOrder(id);
  const { data: activityLogs = [] } = useOrderActivityLog(jointOrder?.[0]?.id);
  const { data: invitedOrderSuppliers } = useInvitedOrderSuppliers(
    jointOrder?.[0]?.id || ""
  );
  const { data: supplierActivity } = useOrderSupplierActivity(
    jointOrder?.[0]?.id
  );

  const { data: multipleOffersOnOrders, isLoading: isLoadingOffers } =
    useMultipleOffersOnOrders(multipleOrderId || "");

  // "Crear OC" for a joint group shows only while it still has no OC — i.e. while the group appears
  // in the awardable-negotiations list. Queried only for awarded joints the user may convert.
  const canCreatePO = can(ABILITIES.CREATE_PURCHASE_ORDERS);
  const isJointAwarded = jointOrder?.[0]?.state === OrderState.SUCCEED;
  const { data: awardable = [] } = useAwardableNegotiations(
    Boolean(isJointAwarded && canCreatePO)
  );
  const hasOcCandidate = awardable.some(
    (c) => c.multipleOrderId === multipleOrderId
  );

  const { mutate: acceptOffer, isPending: isAccepting } = useAcceptOffer(
    () => {
      // Close the award panel and refresh the joint order, its offers and its activity log so the
      // page lands on the freshly-awarded state rather than a stale screen.
      setIsAward(false);
      openNotification(t("tenders.data.acceptOffer"));
      queryClient.invalidateQueries({ queryKey: [queryKeys.JOINT_ORDER, id] });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.MULTIPLE_OFFERS_ON_ORDERS, multipleOrderId],
      });
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ORDER_ACTIVITY_LOG, jointOrder?.[0]?.id],
      });
    },
    () => openNotification(t("newMaterialForm.error"))
  );

  const { mutate: mutateChat, isPending: isLoadingCreateChat } = useCreateChat([
    queryKeys.MULTIPLE_ORDER_CHAT,
  ]);

  const { mutate: mutateMessage, isPending: isLoadingMessage } =
    useCreateMessage([queryKeys.MULTIPLE_ORDER_CHAT]);

  const { mutate: deleteMultipleOrders, isPending: isDeletingOrders } =
    useDeleteMultipleOrders(
      () => {
        openNotification(t("tenders.data.cancelMessage"));
        return navigate("/orders");
      },
      () => openNotification(t("newMaterialForm.error"))
    );

  const {
    mutate: downloadJointBidReport,
    isPending: isDownloadingJointBidReport,
  } = useDownloadJointBidReport();
  const { mutate: downloadComparison, isPending: isDownloadingComparison } =
    useDownloadOrderComparison();

  const {
    multipleOffers,
    flattenedOffers,
    cols,
    selectedOffers,
    ordersWithoutOffers,
  } = useJointBidOffers(jointOrder, multipleOffersOnOrders);

  const handleCancel = () => {
    if (!cancelMessage || !jointOrder || jointOrder.length === 0) return;

    const cancelledOrders = jointOrder.map((order: any) => ({
      ...order,
      state: OrderState.CANCELLED,
      comment: cancelMessage,
    }));

    if (Array.isArray(cancelledOrders) && cancelledOrders.length > 0) {
      deleteMultipleOrders(cancelledOrders);
    }

    setOpen(false);
  };

  const handleItemClick = (item: OfferResponse[]) => {
    if (jointOrder?.[0]?.state === OrderState.OPEN && isCompliant) return;
    setItemToSlide(item);
    setOpenSlide(true);
  };

  if (isLoadingOrders || isLoadingOffers) {
    return <Loader isFullScreen />;
  }

  if (!jointOrder || !multipleOrderId) return null;

  const firstOrder = jointOrder[0];
  const groupRound = Math.max(1, ...jointOrder.map((o) => o.round ?? 1));
  const canAddSupplier =
    canManageOrder &&
    (firstOrder?.state === OrderState.OPEN ||
      firstOrder?.state === OrderState.CONSULTATION);

  const isAwarded = firstOrder?.state === OrderState.SUCCEED;

  const summaryTile = (
    <OfferSummaryTile
      totalOffers={countOfferingSuppliers(flattenedOffers ?? [])}
      materialsCount={jointOrder?.length || 0}
      suppliersCount={
        new Set(
          jointOrder?.flatMap(
            (o: any) => o.suppliers?.map((s: any) => s.id) ?? []
          )
        ).size
      }
    />
  );

  const receivedOffersTile = (
    <Tile className="bid-tile">
      <h3 className="bid-tile__title">{t("tenders.data.receivedOffers")}</h3>
      <OffersOnMultipleOrders
        offOnOrd={multipleOffers}
        handleItemClick={handleItemClick}
        state={firstOrder?.state}
        activity={supplierActivity}
      />
    </Tile>
  );

  const activityTile = (
    <Tile className="bid-tile">
      <h3 className="bid-tile__title">
        {t("activityLog.title", { defaultValue: "Activity Log" })}
      </h3>
      <ActivityTimeline
        logs={activityLogs}
        purchaseRequestId={firstOrder?.id || ""}
      />
    </Tile>
  );

  return (
    <>
      <CancelOrderDialog
        open={open}
        cancelMessage={cancelMessage}
        onChangeCancelMessage={setCancelMessage}
        onConfirm={handleCancel}
        onClose={() => setOpen(false)}
        isPending={isDeletingOrders}
        inputId="joint-bid-cancel-comment"
      />

      <SecondRoundModal
        open={secondRoundOpen}
        onClose={() => setSecondRoundOpen(false)}
        orderId={firstOrder.id}
        onLaunched={() => {
          setSecondRoundOpen(false);
          queryClient.invalidateQueries({ queryKey: [queryKeys.JOINT_ORDER, id] });
          queryClient.invalidateQueries({
            queryKey: [queryKeys.MULTIPLE_OFFERS_ON_ORDERS, multipleOrderId],
          });
          queryClient.invalidateQueries({ queryKey: [queryKeys.ORDER_ACTIVITY_LOG, firstOrder?.id] });
        }}
      />

      {/* Not `isAccepting`: the award mutation shows its loading state on the confirm dialog's
          button, so the panel stays visible instead of the page flashing a full-screen loader. */}
      {isLoadingOrders || isLoadingOffers || isDeletingOrders ? (
        <Loader isFullScreen />
      ) : (
        <div className="bid-page">
          <div className="bid-page__section">
            <GoBackArrow />
          </div>

          <Tile className="bid-tile bid-page__header">
            <div className="bid-page__header-main">
              <BidHeader orderName={firstOrder?.name} />
              <p className="bid-tile__info-value">
                {t("tenders.tenderSummary.start")}{" "}
                {formatBiddingDateTime(firstOrder?.opensAt, firstOrder?.startDate)}
                {" - "}
                {t("tenders.tenderSummary.end")}{" "}
                {formatBiddingDateTime(firstOrder?.closesAt, firstOrder?.finishDate)}
              </p>
              <Pill order={firstOrder} />
              <SecondRoundTag round={groupRound} />
            </div>

            <BidToolbar
              canManageOrder={canManageOrder}
              isAward={isAward}
              onCancelAward={() => setIsAward(false)}
            showCancelOrder={
              firstOrder?.state !== OrderState.CANCELLED &&
              firstOrder?.state !== OrderState.SUCCEED
            }
            onCancelOrder={() => setOpen(true)}
            chatCount={multipleOrderChat?.messages?.length || 0}
            onOpenChat={() => setOpenChat(true)}
            showInvite={
              firstOrder?.state !== OrderState.CANCELLED &&
              firstOrder?.state !== OrderState.SUCCEED &&
              firstOrder?.state !== OrderState.CLOSED
            }
            onInvite={() => setOpenInviteModal(true)}
            showDownloadComparison={
              firstOrder?.state === OrderState.CLOSED ||
              firstOrder?.state === OrderState.SUCCEED
            }
            onDownloadComparison={() => downloadComparison(firstOrder?.id)}
            isDownloadingComparison={isDownloadingComparison}
            showDownloadBidReport={firstOrder?.state === OrderState.SUCCEED}
            onDownloadBidReport={() => downloadJointBidReport(multipleOrderId!)}
            isDownloadingBidReport={isDownloadingJointBidReport}
            showDeliveries={firstOrder?.state === OrderState.SUCCEED}
            deliveriesLink={`/deliveries/new?orderId=${firstOrder?.id}`}
            showCreateOc={canCreatePO && hasOcCandidate}
            onCreateOc={() => setCreateOcOpen(true)}
            showAwardTrigger={
              firstOrder?.state === OrderState.CLOSED &&
              Boolean(multipleOffers?.length)
            }
            onAwardTrigger={() => setIsAward(true)}
            showSecondRound={
              firstOrder?.state === OrderState.CLOSED &&
              selectedOffers.length === 0
            }
              onSecondRound={() => setSecondRoundOpen(true)}
            />
          </Tile>

          {isAward || isViewingAward ? (
            <AwardJoint
              offers={cols}
              order={jointOrder}
              onAcceptOffer={acceptOffer}
              onCancel={() => {
                setIsAward(false);
                setIsViewingAward(false);
              }}
              isAwarding={isAccepting}
              readOnly={isViewingAward}
            />
          ) : (
            <BidAwardedTabs
              firstTabLabel={
                isAwarded
                  ? t("tenders.data.tabAdjudication")
                  : t("tenders.data.tabSummary")
              }
              adjudication={
                <>
                  {isAwarded && Boolean(multipleOffers?.length) && (
                    <div className="bid-page__view-award">
                      <Button
                        kind="tertiary"
                        size="sm"
                        onClick={() => setIsViewingAward(true)}
                      >
                        {t("award.viewComparison")}
                      </Button>
                    </div>
                  )}
                  {isAwarded && selectedOffers && (
                    <BidResultTable
                      motive={firstOrder?.offerSelectionComment}
                      fileUrl={firstOrder?.offerSelectionFileUrl}
                      selectedOffers={selectedOffers}
                      ordersWithoutOffers={ordersWithoutOffers}
                      title={t("tenders.seller.acceptedJoint")}
                    />
                  )}
                  {summaryTile}
                  <OrderConditionsTile order={firstOrder} />
                <ServicesToQuoteCard services={servicesToQuoteOf(jointOrder)} />
                  <JointMaterialsOverviewTile orders={jointOrder} />
                  {firstOrder?.state === OrderState.CLOSED &&
                    multipleOffers[0]?.length === 0 && (
                      <AlertBanner title={"tenders.data.noOffersMessage"} />
                    )}
                  {firstOrder?.state === OrderState.CANCELLED && (
                    <AlertBanner
                      title={"tenders.data.cancelledMessage"}
                      comments={firstOrder?.comment}
                    />
                  )}
                </>
              }
              suppliers={
                <>
                  <SuppliersSelected
                    suppliers={firstOrder?.suppliers}
                    invitedSuppliers={invitedOrderSuppliers}
                    activity={supplierActivity}
                    // C5: allow adding a supplier while the quotation is running — bidding
                    // (OPEN) or Q&A (CONSULTATION) — but not once it has closed/adjudicated.
                    onAddSupplier={
                      canAddSupplier ? () => setOpenInviteModal(true) : undefined
                    }
                  />
                  {receivedOffersTile}
                </>
              }
              audit={activityTile}
            />
          )}
        </div>
      )}

      <ComposedModal
        open={openSlide}
        onClose={() => setOpenSlide(false)}
        size="md"
      >
        <ModalHeader title={t("offer.detailTitle")} />
        <ModalBody>
          {itemToSlide && (
            <Offer offer={itemToSlide as any} type={firstOrder?.type} />
          )}
        </ModalBody>
      </ComposedModal>

      <ComposedModal
        open={openChat}
        onClose={() => setOpenChat(false)}
        size="md"
      >
        <ModalHeader title={t("chat.title")} />
        <ModalBody hasForm>
          <Chat
            isOrder
            isMultiple
            chat={multipleOrderChat}
            order={firstOrder}
            onCreateChat={mutateChat}
            isLoadingChat={
              isLoadingChat || isLoadingMessage || isLoadingCreateChat
            }
            onCreateMessage={mutateMessage}
          />
        </ModalBody>
      </ComposedModal>

      <ComposedModal
        open={openInviteModal}
        onClose={() => setOpenInviteModal(false)}
        size="sm"
      >
        <ModalHeader title={t("orderByRequest.inviteSupplier")} />
        <ModalBody hasForm>
          <InviteSupplier
            existingOrderIds={(jointOrder ?? [])
              .map((order: any) => order.id)
              .filter(Boolean)}
            onCloseInvitation={() => setOpenInviteModal(false)}
          />
        </ModalBody>
      </ComposedModal>

      <CreateOcModal
        open={createOcOpen}
        onClose={() => setCreateOcOpen(false)}
        filter={{ multipleOrderId: multipleOrderId ?? undefined }}
      />
    </>
  );
};

export default JointBuyerBid;
